import { lazy, Suspense, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/use-async';
import {
  ApiKey,
  Application,
  Credential,
  DesiredModule,
  GrantRule,
  SelfModule,
} from '../lib/types';
import { useProfile } from '../lib/profile';
import { ApplicationState } from './Applications';
import { ChangeRequestDiff } from '../components/ChangeRequestDiff';
import { daysUntil, formatDate, formatDateTime, relativeTime } from '../lib/format';
import { Badge, Card, Checkbox, ConfirmDelete, Empty, Field, Modal, Notice, PageHead, Tabs } from '../components/ui';
// Loaded on demand: the code editor and its language support are a third of
// the bundle, and most visits to this page never open the tab.
const FilterEditor = lazy(() =>
  import('../components/FilterEditor').then((module) => ({ default: module.FilterEditor })),
);
import { PermissionMatrix } from '../components/PermissionMatrix';
import { CreateRoleDialog } from './Roles';
import {
  ApplicationFormFields,
  ApplicationFormValues,
  applicationFormComplete,
  applicationFormFrom,
  applicationPayload,
} from '../components/ApplicationForm';

type Tab = 'access' | 'permissions' | 'filter' | 'roles' | 'keys' | 'consents';

export function ApplicationDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const profile = useProfile();
  const [tab, setTab] = useState<Tab>('access');
  const application = useAsync(() => api.get<Application>(`/api/applications/${id}`), [id]);
  const [deleting, setDeleting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (application.error) return <Notice tone="danger">{application.error}</Notice>;
  if (!application.data) return <p className="muted">Loading...</p>;

  const open = application.data.openRequest ?? null;
  const pending = application.data.state === 'pending';

  async function withdraw(): Promise<void> {
    try {
      await api.delete(`/api/applications/${id}/request`);
      application.reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'access', label: 'Access and modules' },
    { id: 'permissions', label: 'Permissions' },
    { id: 'filter', label: 'Filter function' },
    ...(profile.isAdmin ? ([{ id: 'roles', label: 'Roles' }] as const) : []),
    { id: 'keys', label: 'API keys' },
    ...(profile.isAdmin ? ([{ id: 'consents', label: 'Consents' }] as const) : []),
  ];

  return (
    <>
      <PageHead
        title={application.data.name}
        description={application.data.description ?? undefined}
        actions={
          <>
            <ApplicationState application={application.data} />
            <button onClick={() => setEditing(true)}>Edit</button>
            <button className="danger" onClick={() => setDeleting(true)}>
              {pending ? 'Discard' : profile.isAdmin ? 'Delete application' : 'Request deletion'}
            </button>
          </>
        }
      />

      {error ? <Notice tone="danger">{error}</Notice> : null}

      {pending ? (
        <Notice tone="warn" title="Waiting for approval">
          This application is not live yet. An administrator has to approve it before it can call
          anything — everything below is what has been asked for.
        </Notice>
      ) : null}

      {open ? (
        <Card
          title={pending ? 'What was asked for' : 'A change is waiting for approval'}
          hint={`submitted by ${open.requested_by_label} on ${new Date(open.requested_at).toLocaleString()}`}
          actions={
            <>
              {profile.isAdmin ? <Link className="link" to="/approvals">Review it</Link> : null}
              <button onClick={() => void withdraw()}>Withdraw</button>
            </>
          }
        >
          {open.note ? <p className="muted">{open.note}</p> : null}
          {open.diff ? <ChangeRequestDiff diff={open.diff} /> : null}
        </Card>
      ) : null}

      <Tabs active={tab} onChange={setTab} tabs={tabs} />

      {tab === 'access' ? <AccessTab application={application.data} onChanged={application.reload} /> : null}
      {tab === 'permissions' ? <GrantsTab application={application.data} onChanged={application.reload} /> : null}
      {tab === 'filter' ? (
        <Suspense fallback={<p className="muted">Loading the editor…</p>}>
          <FilterEditor application={application.data} />
        </Suspense>
      ) : null}
      {tab === 'roles' ? (
        <RolesTab application={application.data} onChanged={application.reload} onEdit={() => setEditing(true)} />
      ) : null}
      {tab === 'keys' ? (
        <KeysTab application={application.data} onChanged={application.reload} />
      ) : null}
      {tab === 'consents' ? <ConsentsTab application={application.data} /> : null}

      {editing ? (
        <EditApplicationDialog
          application={application.data}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            application.reload();
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteApplicationDialog
          application={application.data}
          onClose={() => setDeleting(false)}
          onDeleted={(removed) => (removed ? navigate('/applications') : application.reload())}
        />
      ) : null}
    </>
  );
}

function EditApplicationDialog({
  application,
  onClose,
  onSaved,
}: {
  application: Application;
  onClose: () => void;
  onSaved: () => void;
}) {
  const profile = useProfile();
  const desired = application.desired;
  const [values, setValues] = useState<ApplicationFormValues>(
    desired
      ? {
          name: desired.name,
          description: desired.description ?? '',
          entraClientId: desired.entraClientId ?? '',
          clientCredentials: desired.allowClientCredentials,
          delegated: desired.allowDelegated,
          apiKey: desired.allowApiKey,
          selfManagement: desired.allowSelfManagement,
          redirectUris: desired.redirectUris.join('\n'),
          ipAllowlist: desired.ipAllowlist.join('\n'),
        }
      : applicationFormFrom(application),
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/applications/${application.id}`, {
        ...applicationPayload(values),
        note: note || null,
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={profile.isAdmin ? 'Edit application' : 'Propose a change'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={busy || !applicationFormComplete(values)}>
            {profile.isAdmin ? 'Save' : 'Submit for approval'}
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <ApplicationFormFields values={values} onChange={setValues} />
      <label className="field">
        <span>
          What changed and why? <span className="hint">— read by whoever reviews it</span>
        </span>
        <textarea value={note} onChange={(event) => setNote(event.target.value)} />
      </label>
      <p className="muted tiny">
        {profile.isAdmin
          ? 'Taking an access path away cuts off callers on that path immediately. Module access, permissions and keys stay as they are.'
          : 'The application keeps running on its approved configuration until this is decided.'}
      </p>
    </Modal>
  );
}

function DeleteApplicationDialog({
  application,
  onClose,
  onDeleted,
}: {
  application: Application;
  onClose: () => void;
  onDeleted: (removed: boolean) => void;
}) {
  const profile = useProfile();
  const activeKeys = (application.apiKeys ?? []).filter((key) => key.state === 'active');
  const consents = application.consents ?? [];
  const pending = application.state === 'pending';

  const impact = pending
    ? ['Nothing was ever approved for it, so nothing is cut off — it simply disappears.']
    : [
        activeKeys.length
          ? `${activeKeys.length} active API ${activeKeys.length === 1 ? 'key stops' : 'keys stop'} working - callers get 401 on the next request.`
          : 'No active API key is in use.',
        application.moduleAccess.length
          ? `Access to ${application.moduleAccess.length} ${application.moduleAccess.length === 1 ? 'module is' : 'modules is'} revoked, together with every permission granted here.`
          : 'No module is enabled for it.',
        profile.isAdmin && consents.length
          ? `${consents.length} ${consents.length === 1 ? 'person has' : 'people have'} consented to it acting in their name - those consents fall away.`
          : 'Consents given to it fall away.',
        ...(profile.isAdmin ? [] : ['An administrator has to approve the deletion before any of it happens.']),
      ];

  return (
    <ConfirmDelete
      title={pending ? 'Discard application' : profile.isAdmin ? 'Delete application' : 'Request deletion'}
      entity={<>Delete the application <strong>{application.name}</strong>?</>}
      impact={impact}
      confirmText={!pending && (activeKeys.length || consents.length) ? application.name : undefined}
      confirmLabel={pending ? 'Discard' : profile.isAdmin ? 'Delete application' : 'Request deletion'}
      onClose={onClose}
      onConfirm={async () => {
        const result = await api.delete<{ removed: boolean }>(`/api/applications/${application.id}`);
        onDeleted(result?.removed ?? true);
      }}
    />
  );
}

function AccessTab({ application, onChanged }: { application: Application; onChanged: () => void }) {
  const profile = useProfile();
  const modules = useAsync(() => api.get<SelfModule[]>('/api/me/modules'));
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const byKey = new Map((modules.data ?? []).map((module) => [module.key, module]));
  const desired = application.desired?.modules ?? [];
  const approved = new Map(application.moduleAccess.map((access) => [access.module_key, access]));

  // The rows are the union of what is asked for and what is live, so a module
  // on its way in and one on its way out are both visible as such.
  const rows = [
    ...desired.map((entry) => ({ moduleKey: entry.moduleKey, wanted: true })),
    ...application.moduleAccess
      .filter((access) => !desired.some((entry) => entry.moduleKey === access.module_key))
      .map((access) => ({ moduleKey: access.module_key, wanted: false })),
  ];
  const available = (modules.data ?? []).filter(
    (module) => !desired.some((entry) => entry.moduleKey === module.key),
  );

  function moduleIdOf(moduleKey: string): string | undefined {
    return byKey.get(moduleKey)?.id ?? approved.get(moduleKey)?.module_id;
  }

  async function enable(moduleId: string): Promise<void> {
    setBusy(true);
    try {
      await api.put(`/api/applications/${application.id}/modules/${moduleId}`, {});
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  async function revoke(moduleKey: string): Promise<void> {
    const moduleId = moduleIdOf(moduleKey);
    if (!moduleId) return;
    if (!window.confirm('Revoke access to this module?')) return;
    await api.delete(`/api/applications/${application.id}/modules/${moduleId}`);
    onChanged();
  }

  const writingWithoutBinding =
    profile.isAdmin &&
    application.allow_client_credentials &&
    application.moduleAccess.some((access) => !access.pinned_credential_id);

  return (
    <>
      {writingWithoutBinding ? (
        <Notice tone="warn" title="Changing originator in the target API">
          This application acts with application permissions and is not bound to a fixed credential. On write
          operations the originator in the target system therefore changes from call to call. Anyone who wants to
          trace that later should pick a fixed credential.
        </Notice>
      ) : null}

      <Card
        title="Reachable from"
        hint="holds for every access path; a key's own list can only narrow it further"
      >
        {(application.desired?.ipAllowlist ?? application.ip_allowlist).length ? (
          <div className="row">
            {(application.desired?.ipAllowlist ?? application.ip_allowlist).map((entry) => (
              <span key={entry} className="mono">
                {entry}
              </span>
            ))}
          </div>
        ) : (
          <p className="muted">
            From anywhere. Bind the application to addresses under <strong>Edit</strong>.
          </p>
        )}
      </Card>

      <Card title="Modules" tight>
        {rows.length ? (
          <table>
            <thead>
              <tr>
                <th>Module</th>
                <th>State</th>
                {profile.isAdmin ? <th>Quota</th> : null}
                {profile.isAdmin ? <th>Credential</th> : null}
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const access = approved.get(row.moduleKey);
                const moduleId = moduleIdOf(row.moduleKey);
                return (
                  <tr key={row.moduleKey}>
                    <td className="mono">{row.moduleKey}</td>
                    <td>
                      {access && row.wanted ? (
                        <Badge tone="ok">live</Badge>
                      ) : row.wanted ? (
                        <Badge tone="warn">waiting for approval</Badge>
                      ) : (
                        <Badge tone="warn">removal waiting</Badge>
                      )}
                    </td>
                    {profile.isAdmin ? (
                      <td>
                        {!access || access.rate_limit === null
                          ? 'unlimited'
                          : `${access.rate_limit} per ${access.rate_window_seconds} s`}
                      </td>
                    ) : null}
                    {profile.isAdmin ? (
                      <td>{access?.pinned_credential_label ?? <span className="muted">round-robin</span>}</td>
                    ) : null}
                    <td>
                      <div className="row">
                        {profile.isAdmin && access && moduleId ? (
                          <button className="small" onClick={() => setEditing(moduleId)}>
                            Edit
                          </button>
                        ) : null}
                        {row.wanted ? (
                          <button className="small danger" onClick={() => void revoke(row.moduleKey)}>
                            Revoke
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <Empty>This application has no access to any module yet.</Empty>
        )}
      </Card>

      {available.length ? (
        <Card
          title="Ask for a module"
          hint={profile.isAdmin ? undefined : 'an administrator decides whether it is granted'}
        >
          <div className="row">
            {available.map((module) => (
              <button
                key={module.id}
                disabled={busy}
                onClick={() => (profile.isAdmin ? setEditing(module.id) : void enable(module.id))}
              >
                {module.name}
              </button>
            ))}
          </div>
        </Card>
      ) : null}

      {editing ? (
        <ModuleAccessDialog
          applicationId={application.id}
          moduleId={editing}
          existing={application.moduleAccess.find((access) => access.module_id === editing)}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function ModuleAccessDialog({
  applicationId,
  moduleId,
  existing,
  onClose,
  onSaved,
}: {
  applicationId: string;
  moduleId: string;
  existing?: { rate_limit: number | null; rate_window_seconds: number; pinned_credential_id: string | null };
  onClose: () => void;
  onSaved: () => void;
}) {
  const credentials = useAsync(
    () => api.get<Credential[]>(`/api/admin/modules/${moduleId}/credentials`),
    [moduleId],
  );
  const [rateLimit, setRateLimit] = useState(existing?.rate_limit?.toString() ?? '');
  const [window_, setWindow] = useState(existing?.rate_window_seconds?.toString() ?? '3600');
  const [pinned, setPinned] = useState(existing?.pinned_credential_id ?? '');
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    try {
      await api.put(`/api/applications/${applicationId}/modules/${moduleId}`, {
        rateLimit: rateLimit === '' ? null : Number(rateLimit),
        rateWindowSeconds: Number(window_),
        pinnedCredentialId: pinned || null,
      });
      onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title="Module access"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void save()}>
            Save
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Quota" hint="requests per time window; empty means unlimited">
        <input type="number" value={rateLimit} onChange={(event) => setRateLimit(event.target.value)} />
      </Field>
      <Field label="Time window in seconds">
        <input type="number" value={window_} onChange={(event) => setWindow(event.target.value)} />
      </Field>
      <Field label="Fixed credential" hint="without a selection the pool is used in turn">
        <select value={pinned} onChange={(event) => setPinned(event.target.value)}>
          <option value="">Round-robin</option>
          {(credentials.data ?? []).map((credential) => (
            <option key={credential.id} value={credential.id}>
              {credential.label}
            </option>
          ))}
        </select>
      </Field>
    </Modal>
  );
}

function GrantsTab({
  application,
  onChanged,
}: {
  application: Application;
  onChanged: () => void;
}) {
  const profile = useProfile();
  const modules = useAsync(() => api.get<SelfModule[]>('/api/me/modules'));
  const desired: DesiredModule[] = application.desired?.modules ?? [];
  const [moduleKey, setModuleKey] = useState(desired[0]?.moduleKey ?? '');
  const [draft, setDraft] = useState<GrantRule[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const module = (modules.data ?? []).find((entry) => entry.key === moduleKey);
  const stored = (desired.find((entry) => entry.moduleKey === moduleKey)?.rules ?? []).map(
    (rule) => ({ resource: rule.resource, action: rule.action, effect: rule.effect }),
  );
  const rules = draft ?? stored;
  const dirty = draft !== null;

  async function save(): Promise<void> {
    if (!module) return;
    setSaving(true);
    setError(null);
    try {
      await api.put(`/api/applications/${application.id}/modules/${module.id}/grants`, {
        rules: rules.map((rule) => ({ resource: rule.resource, action: rule.action, effect: rule.effect })),
      });
      setDraft(null);
      setMessage(
        profile.isAdmin
          ? 'Saved. The change takes effect immediately.'
          : 'Submitted. It takes effect once an administrator approves it.',
      );
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }

  if (!desired.length) {
    return <Empty>Ask for a module first, then there are permissions to grant here.</Empty>;
  }

  return (
    <>
      {message ? <Notice tone="ok">{message}</Notice> : null}
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Card
        title="Permissions of the application"
        hint="The outer scope: no consent and no key can allow more"
        actions={
          <div className="row">
            <select
              value={moduleKey}
              onChange={(event) => {
                setModuleKey(event.target.value);
                setDraft(null);
                setMessage(null);
              }}
            >
              {desired.map((entry) => (
                <option key={entry.moduleKey} value={entry.moduleKey}>
                  {(modules.data ?? []).find((module) => module.key === entry.moduleKey)?.name ??
                    entry.moduleKey}
                </option>
              ))}
            </select>
            <button className="primary small" onClick={() => void save()} disabled={!dirty || saving}>
              {saving ? 'Saving...' : profile.isAdmin ? 'Save' : 'Submit for approval'}
            </button>
          </div>
        }
        tight
      >
        <PermissionMatrix resources={module?.resources ?? []} rules={rules} onChange={setDraft} />
      </Card>
    </>
  );
}

function RolesTab({
  application,
  onChanged,
  onEdit,
}: {
  application: Application;
  onChanged: () => void;
  onEdit: () => void;
}) {
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roles = application.roles ?? [];

  async function toggleSelfManagement(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/applications/${application.id}`, {
        name: application.name,
        allowClientCredentials: application.allow_client_credentials,
        allowDelegated: application.allow_delegated,
        allowApiKey: application.allow_api_key,
        allowSelfManagement: !application.allow_self_management,
      });
      onChanged();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Card
        title="Self-management"
        hint="Lets the application create its own roles and hand them to its users through /api/self"
        actions={
          application.allow_client_credentials ? (
            <button className="small" onClick={() => void toggleSelfManagement()} disabled={busy}>
              {application.allow_self_management ? 'Disable' : 'Enable'}
            </button>
          ) : application.allow_api_key ? (
            <button className="small" onClick={() => void toggleSelfManagement()} disabled={busy}>
              {application.allow_self_management ? 'Disable' : 'Enable'}
            </button>
          ) : (
            <button className="small" onClick={onEdit}>
              Edit access paths
            </button>
          )
        }
      >
        {application.allow_client_credentials || application.allow_api_key ? (
          <p className="muted">
            {application.allow_self_management
              ? 'Enabled. The application manages the roles below itself - with its application token, or with an API key marked as a management key on the keys tab. It cannot widen its own scope: a role is always intersected with the permissions granted here.'
              : 'Disabled. Only administrators manage the roles below.'}
          </p>
        ) : (
          <p className="muted">
            Needs a way in for the application itself: application permissions (client_credentials) or API key
            access. Enable one under <strong>Edit access paths</strong>.
          </p>
        )}
      </Card>

      <Card
        title="Roles of this application"
        hint="count only for people acting through this application; global roles apply here as well"
        actions={
          <button className="primary small" onClick={() => setCreating(true)}>
            Create role
          </button>
        }
        tight
      >
        {roles.length ? (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Entra groups</th>
                <th className="num">Members</th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr key={role.id}>
                  <td>
                    <Link to={`/roles/${role.id}`}>{role.name}</Link>
                  </td>
                  <td className="muted">{role.description ?? '—'}</td>
                  <td>
                    {role.groups.length ? (
                      <div className="row">
                        {role.groups.map((group) => (
                          <Badge key={group.id}>{group.group_name ?? group.entra_group_id}</Badge>
                        ))}
                      </div>
                    ) : (
                      <span className="muted">assigned manually only</span>
                    )}
                  </td>
                  <td className="num">{role.memberCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No role of its own yet. A role created here applies here only.</Empty>
        )}
      </Card>

      {creating ? (
        <CreateRoleDialog
          applicationId={application.id}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            onChanged();
          }}
        />
      ) : null}
    </>
  );
}

function KeysTab({ application, onChanged }: { application: Application; onChanged: () => void }) {
  // Only an administrator hands out a management key: it reaches the
  // self-service API, which is more than the application's own scope.
  const profileIsAdmin = useProfile().isAdmin;
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ secret: string; label: string } | null>(null);
  const [restricting, setRestricting] = useState<ApiKey | null>(null);

  async function revoke(keyId: string): Promise<void> {
    if (!window.confirm('Revoke this key? Applications using it lose access immediately.'))
      return;
    await api.delete(`/api/applications/${application.id}/keys/${keyId}`);
    onChanged();
  }

  if (!application.allow_api_key) {
    return (
      <Notice tone="info">
        Access via API key is not enabled for this application.
      </Notice>
    );
  }

  return (
    <>
      {created ? (
        <Notice tone="ok" title="Key created - only visible now">
          <span className="mono" style={{ wordBreak: 'break-all' }}>{created.secret}</span>
          <br />
          <button className="small" style={{ marginTop: 8 }} onClick={() => void navigator.clipboard.writeText(created.secret)}>
            Copy to clipboard
          </button>{' '}
          <button className="small" onClick={() => setCreated(null)}>
            Hide
          </button>
        </Notice>
      ) : null}

      <Card
        title="API keys"
        hint="A key inherits the scope of the application; rules narrow it down"
        actions={
          <button className="primary small" onClick={() => setCreating(true)}>
            Create key
          </button>
        }
        tight
      >
        {application.apiKeys?.length ? (
          <table>
            <thead>
              <tr>
                <th>Label</th>
                <th>Identifier</th>
                <th>Expires</th>
                <th>Last used</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {application.apiKeys.map((key) => (
                <tr key={key.id}>
                  <td>
                    {key.label}
                    {key.can_manage ? (
                      <>
                        {' '}
                        <Badge tone="accent">manages roles</Badge>
                      </>
                    ) : null}
                  </td>
                  <td className="mono muted">{key.key_id}</td>
                  <td>
                    {formatDate(key.expires_at)}{' '}
                    {key.state === 'active' && daysUntil(key.expires_at) <= 30 ? (
                      <Badge tone="warn">in {daysUntil(key.expires_at)} days</Badge>
                    ) : null}
                  </td>
                  <td className="muted">{relativeTime(key.last_used_at)}</td>
                  <td>
                    <Badge tone={key.state === 'active' ? 'ok' : ''}>{key.state}</Badge>
                  </td>
                  <td>
                    {key.state === 'active' ? (
                      <div className="row">
                        <button className="small" onClick={() => setRestricting(key)}>
                          Restrict
                        </button>
                        <button className="small danger" onClick={() => void revoke(key.id)}>
                          Revoke
                        </button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty>No key created yet.</Empty>
        )}
      </Card>

      {creating ? (
        <CreateKeyDialog
          applicationId={application.id}
          selfManagement={application.allow_self_management && profileIsAdmin}
          onClose={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreated(result);
            onChanged();
          }}
        />
      ) : null}

      {restricting ? (
        <RestrictKeyDialog
          application={application}
          apiKey={restricting}
          onClose={() => setRestricting(null)}
        />
      ) : null}
    </>
  );
}

function CreateKeyDialog({
  applicationId,
  selfManagement,
  onClose,
  onCreated,
}: {
  applicationId: string;
  /** Whether the application may manage itself — only then can a key be a management key. */
  selfManagement: boolean;
  onClose: () => void;
  onCreated: (result: { secret: string; label: string }) => void;
}) {
  const [label, setLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState(
    new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10),
  );
  const [ips, setIps] = useState('');
  const [canManage, setCanManage] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(): Promise<void> {
    try {
      const result = await api.post<{ secret: string; label: string }>(
        `/api/applications/${applicationId}/keys`,
        {
          label,
          expiresAt: new Date(`${expiresAt}T23:59:59Z`).toISOString(),
          ipAllowlist: ips.split(/\s+/).map((value) => value.trim()).filter(Boolean),
          canManage: selfManagement && canManage,
        },
      );
      onCreated(result);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    }
  }

  return (
    <Modal
      title="Create API key"
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void submit()} disabled={!label}>
            Create
          </button>
        </>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Field label="Label" hint="what is it used for">
        <input type="text" value={label} onChange={(event) => setLabel(event.target.value)} />
      </Field>
      <Field label="Expires on" hint="an expiry date is mandatory">
        <input type="date" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
      </Field>
      <Field label="Allowed IP addresses" hint="optional; one address or range per line">
        <textarea style={{ minHeight: 70 }} value={ips} onChange={(event) => setIps(event.target.value)} />
      </Field>
      {selfManagement ? (
        <Field label="Management" hint="a management key also reaches /api/self">
          <Checkbox
            checked={canManage}
            onChange={setCanManage}
            label="This key may manage the application's roles - create them, grant them, assign them"
          />
        </Field>
      ) : null}
      <p className="muted tiny">
        The secret is shown exactly once. After that only its hash exists - it cannot be recovered, only
        replaced.
      </p>
    </Modal>
  );
}

function RestrictKeyDialog({
  application,
  apiKey,
  onClose,
}: {
  application: Application;
  apiKey: ApiKey;
  onClose: () => void;
}) {
  const [moduleId, setModuleId] = useState(application.moduleAccess[0]?.module_id ?? '');
  // Narrowing a key is not a proposal — it can only take away — so the owner
  // reads the resource matrix from the same list the rest of the page uses.
  const modules = useAsync(() => api.get<SelfModule[]>('/api/me/modules'));
  const resources = (modules.data ?? []).find((module) => module.id === moduleId)?.resources ?? [];
  const stored = useAsync(
    () =>
      moduleId
        ? api.get<GrantRule[]>(
            `/api/applications/${application.id}/keys/${apiKey.id}/modules/${moduleId}/grants`,
          )
        : Promise.resolve([]),
    [moduleId, apiKey.id],
  );
  const [draft, setDraft] = useState<GrantRule[] | null>(null);
  const rules = draft ?? (stored.data ?? []);

  async function save(): Promise<void> {
    await api.put(`/api/applications/${application.id}/keys/${apiKey.id}/modules/${moduleId}/grants`, {
      rules: rules.map((rule) => ({ resource: rule.resource, action: rule.action, effect: rule.effect })),
    });
    onClose();
  }

  return (
    <Modal
      title={`Restrict key "${apiKey.label}"`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={() => void save()}>
            Save
          </button>
        </>
      }
    >
      <Notice tone="info">
        Without rules of its own this key may do everything the application may. As soon as something is listed
        here, only the intersection applies.
      </Notice>
      <select value={moduleId} onChange={(event) => { setModuleId(event.target.value); setDraft(null); }}>
        {application.moduleAccess.map((access) => (
          <option key={access.module_id} value={access.module_id}>
            {access.module_name}
          </option>
        ))}
      </select>
      <div style={{ marginTop: 12 }}>
        <PermissionMatrix resources={resources} rules={rules} onChange={setDraft} />
      </div>
    </Modal>
  );
}

function ConsentsTab({ application }: { application: Application }) {
  if (!application.allow_delegated) {
    return <Notice tone="info">This application does not accept consents from users.</Notice>;
  }

  return (
    <Card title="Granted consents" hint="Who has allowed this application to act in their name" tight>
      {application.consents?.length ? (
        <table>
          <thead>
            <tr>
              <th>Person</th>
              <th>Module</th>
              <th>Granted</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {application.consents.map((consent) => (
              <tr key={consent.id}>
                <td>
                  {consent.user_name ?? consent.user_email}
                  <div className="muted tiny">
                    {consent.user_email}
                  </div>
                </td>
                <td className="mono">{consent.module_key}</td>
                <td className="nowrap">{formatDateTime(consent.granted_at)}</td>
                <td>
                  <Badge tone={consent.state === 'active' ? 'ok' : ''}>{consent.state}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <Empty>Nobody has granted this application anything yet.</Empty>
      )}
    </Card>
  );
}
